
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Edit, Loader2 } from "lucide-react";

interface EditColumnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId: string;
  currentName: string;
  onRefresh: () => void;
}

export function EditColumnDialog({
  open,
  onOpenChange,
  columnId,
  currentName,
  onRefresh,
}: EditColumnDialogProps) {
  const [columnName, setColumnName] = useState(currentName);
  const queryClient = useQueryClient();

  const updateColumnMutation = useMutation({
    mutationFn: async (newName: string) => {
      const { error } = await supabase
        .from("kanban_columns")
        .update({ name: newName.trim() })
        .eq("id", columnId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-columns"] });
      toast.success("Coluna renomeada com sucesso!");
      onRefresh();
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Error updating column:", error);
      toast.error("Erro ao renomear coluna");
    },
  });

  const handleSave = () => {
    if (!columnName.trim()) {
      toast.error("O nome da coluna não pode estar vazio");
      return;
    }
    updateColumnMutation.mutate(columnName);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Renomear Coluna
          </DialogTitle>
          <DialogDescription>
            Digite o novo nome para a coluna.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="column-name">Nome da Coluna</Label>
            <Input
              id="column-name"
              value={columnName}
              onChange={(e) => setColumnName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nome da coluna"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!columnName.trim() || updateColumnMutation.isPending}
          >
            {updateColumnMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
